import { useContext, useState } from 'react';
import RestrictedToPortfolioMode from '@/components/RestrictedToPortfolioMode';
import PortfolioMode from '@/lib/constants/PortfolioMode.enum';
import ChallengeLinkSharing from './options/ChallengeLinkSharing';
import ChallengeNameEditor from './ChallengeNameEditor';
import DeleteChallenge from './options/DeleteChallenge';
import CopyChallenge from './options/CopyChallenge';
import HeaderWrapper from './HeaderWrapper';
import InnerHeaderWrapper from './InnerHeaderWrapper';
import ChallengeName from './ChallengeName';
import TasksButton from './TasksButton';
import ChallengeContext from '../ChallengeContext';
import RowContext from '@/components/ChallengePortfolio/NormalView/Rows/Row/RowContext';
import useRandomizedId from '@/lib/utils/useRandomizedId';
import { Divider, IconButton, Tooltip } from '@mui/material';
import { SettingsOutlined as OptionsIcon } from '@mui/icons-material';
import MenuWithLazyPersistedMounting from '@/components/Menu/MenuWithLazyPersistedMounting';
import EditNameOption from './options/EditNameOption';
import UploadThumbnail from '../../../portfolio-item/PortfolioItemThumbnailHeader/UploadThumbnail';
import FormattedMessageWithChallengeNaming from '@/components/FormattedMessageWithChallengeNaming';
import useForceStateUpdate from '@/lib/utils/useForceStateUpdate';
import ModelInfo from './ModelInfo';
import ModelType from '@/lib/constants/challengeModel/ModelType.enum';

type ChallengeHeaderProps = {
  onTasksOpen: () => void;
  loading?: boolean;
};

// TODO when challenge is loading, disable this (locked prop)
const ChallengeHeader = ({ onTasksOpen, loading }: ChallengeHeaderProps) => {
  const [challenge] = useContext(ChallengeContext);
  const row = useContext(RowContext);

  const [nameEditing, setNameEditing] = useState(false);

  const optionsButtonId = useRandomizedId('challenge-options-button');
  const optionsMenuId = useRandomizedId('challenge-options-menu');

  const [optionsMenuAnchorElement, setOptionsMenuAnchorElement] =
    useState<HTMLButtonElement | null>(null);
  const optionsMenuOpen = optionsMenuAnchorElement !== null;

  const openOptionsMenu = (e: React.MouseEvent<HTMLButtonElement>): void => {
    setOptionsMenuAnchorElement(e.currentTarget);
  };

  const closeOptionsMenu = (): void => {
    setOptionsMenuAnchorElement(null);
  };

  const forceStateUpdate = useForceStateUpdate();

  if (nameEditing) {
    return <ChallengeNameEditor onClose={() => setNameEditing(false)} />;
  }

  return (
    <HeaderWrapper>
      <InnerHeaderWrapper>
        <ChallengeName>{challenge.name}</ChallengeName>
        <RestrictedToPortfolioMode mode={PortfolioMode.STUDENT}>
          <Tooltip
            title={
              <FormattedMessageWithChallengeNaming id="portfolio.challenge.options.button_tooltip" />
            }
          >
            <IconButton
              id={optionsButtonId}
              onClick={openOptionsMenu}
              aria-controls={optionsMenuOpen ? optionsMenuId : undefined}
              aria-expanded={optionsMenuOpen ? 'true' : undefined}
              aria-haspopup
            >
              <OptionsIcon />
            </IconButton>
          </Tooltip>
          <MenuWithLazyPersistedMounting
            id={optionsMenuId}
            labelledById={optionsButtonId}
            anchorEl={optionsMenuAnchorElement}
            open={optionsMenuOpen}
            onClose={closeOptionsMenu}
          >
            <ModelInfo variant={ModelType.CHALLENGE} />
            <EditNameOption
              onClick={() => {
                closeOptionsMenu();

                setNameEditing(true);
              }}
            />
            <Divider />
            {challenge.thumbnail === null && (
              <UploadThumbnail
                variant="add"
                onForceMenuStateUpdate={forceStateUpdate}
                onCloseMenu={closeOptionsMenu}
              />
            )}
            <ChallengeLinkSharing />
            <Divider />
            {row && <CopyChallenge />}
            <DeleteChallenge />
          </MenuWithLazyPersistedMounting>
        </RestrictedToPortfolioMode>
      </InnerHeaderWrapper>
      {!loading && <TasksButton onClick={() => onTasksOpen()} />}
    </HeaderWrapper>
  );
};

export default ChallengeHeader;
